import { useState, useEffect, useRef } from 'react'
import Lottie from './Lottie'
import './AppDock.css'

function AppDock({ onAppSelect, selectedApp }) {
  const [hoveredApp, setHoveredApp] = useState(null)
  const [isVisible, setIsVisible] = useState(true) // Dock visible par défaut
  const [houseAnimation, setHouseAnimation] = useState(null)
  const [cartsAnimation, setCartsAnimation] = useState(null)
  const [analyticsAnimation, setAnalyticsAnimation] = useState(null)
  const [socialAnimation, setSocialAnimation] = useState(null)
  const [settingsAnimation, setSettingsAnimation] = useState(null)
  const houseLottieRef = useRef(null)
  const cartsLottieRef = useRef(null)
  const analyticsLottieRef = useRef(null)
  const socialLottieRef = useRef(null)
  const settingsLottieRef = useRef(null)
  const hideTimeoutRef = useRef(null)

  // Gérer l'apparition/disparition du dock selon la position de la souris.
  // Sans pointeur fin (écran tactile), il n'y a pas de survol : le dock reste affiché.
  useEffect(() => {
    const finePointer = typeof window.matchMedia === 'function'
      ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
      : true
    if (!finePointer) {
      setIsVisible(true)
      return undefined
    }

    const handleMouseMove = (e) => {
      const windowHeight = window.innerHeight
      const mouseY = e.clientY
      const triggerZone = 100 // Zone de déclenchement en pixels depuis le bas
      
      // Si la souris est dans la zone de déclenchement (bas de l'écran)
      if (mouseY >= windowHeight - triggerZone) {
        // Annuler le timer de masquage si existant
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setIsVisible(true)
      } else {
        // Si le dock est visible et la souris sort de la zone
        if (isVisible && !hideTimeoutRef.current) {
          // Attendre 800ms avant de masquer (délai comme macOS)
          hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false)
            hideTimeoutRef.current = null
          }, 800)
        }
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [isVisible])

  useEffect(() => {
    const syncViewport = () => {
      const root = document.documentElement
      const visual = window.visualViewport
      const keyboard = visual
        ? Math.max(0, window.innerHeight - visual.height - visual.offsetTop)
        : 0
      const narrow = window.matchMedia('(max-width: 768px)').matches
      root.style.setProperty('--keyboard-inset', `${Math.round(keyboard)}px`)
      root.classList.toggle('keyboard-open', narrow && keyboard > 80)
      if (!narrow) return
      const dock = document.querySelector('.app-dock')
      if (dock) {
        root.style.setProperty('--dock-h', `${Math.round(dock.getBoundingClientRect().height)}px`)
      }
    }

    syncViewport()
    const visual = window.visualViewport
    visual?.addEventListener('resize', syncViewport)
    visual?.addEventListener('scroll', syncViewport)
    window.addEventListener('resize', syncViewport)
    const dock = document.querySelector('.app-dock')
    const observer = (typeof ResizeObserver === 'function' && dock)
      ? new ResizeObserver(syncViewport)
      : null
    if (observer && dock) observer.observe(dock)
    return () => {
      visual?.removeEventListener('resize', syncViewport)
      visual?.removeEventListener('scroll', syncViewport)
      window.removeEventListener('resize', syncViewport)
      observer?.disconnect()
      document.documentElement.classList.remove('keyboard-open')
      document.documentElement.style.removeProperty('--keyboard-inset')
    }
  }, [])

  // Charger les animations Lottie
  useEffect(() => {
    fetch('/house_animation.json')
      .then(response => {
        if (!response.ok) throw new Error('Fichier non trouvé')
        return response.json()
      })
      .then(data => setHouseAnimation(data))
      .catch(error => {
        console.warn('Animation house_animation.json non trouvée, utilisation du placeholder')
        setHouseAnimation(null)
      })
    
    fetch('/Cadis_animation.json')
      .then(response => {
        if (!response.ok) throw new Error('Fichier non trouvé')
        return response.json()
      })
      .then(data => setCartsAnimation(data))
      .catch(error => {
        console.warn('Animation Cadis_animation.json non trouvée, utilisation du placeholder')
        setCartsAnimation(null)
      })
    
    fetch('/Analytics_animation.json')
      .then(response => {
        if (!response.ok) throw new Error('Fichier non trouvé')
        return response.json()
      })
      .then(data => setAnalyticsAnimation(data))
      .catch(error => {
        console.warn('Animation Analytics_animation.json non trouvée, utilisation du placeholder')
        setAnalyticsAnimation(null)
      })
    
    fetch('/Social media_animation.json')
      .then(response => {
        if (!response.ok) throw new Error('Fichier non trouvé')
        return response.json()
      })
      .then(data => setSocialAnimation(data))
      .catch(error => {
        console.warn('Animation Social media_animation.json non trouvée, utilisation du placeholder')
        setSocialAnimation(null)
      })
    
    fetch('/settings_animation.json')
      .then(response => {
        if (!response.ok) throw new Error('Fichier non trouvé')
        return response.json()
      })
      .then(data => {
        setSettingsAnimation(data)
        console.log('Animation settings chargée avec succès')
      })
      .catch(error => {
        console.warn('Animation settings_animation.json non trouvée, utilisation du placeholder', error)
        setSettingsAnimation(null)
      })
  }, [])

  // Fonction pour jouer l'animation
  const playAnimation = (appId) => {
    let lottieRef = null
    if (appId === 'house') {
      lottieRef = houseLottieRef
    } else if (appId === 'carts') {
      lottieRef = cartsLottieRef
    } else if (appId === 'analytics') {
      lottieRef = analyticsLottieRef
    } else if (appId === 'social') {
      lottieRef = socialLottieRef
    } else if (appId === 'settings') {
      lottieRef = settingsLottieRef
    }

    if (lottieRef && lottieRef.current) {
      try {
        // Utiliser l'API moderne de Lottie React
        if (typeof lottieRef.current.goToAndPlay === 'function') {
          lottieRef.current.setSpeed(1)
          lottieRef.current.goToAndPlay(0)
        } else if (lottieRef.current.animationItem) {
          // Fallback pour l'ancienne API
          const instance = lottieRef.current.animationItem
          if (instance && typeof instance.goToAndPlay === 'function') {
            instance.loop = false
            instance.goToAndPlay(0, true)
          }
        }
      } catch (error) {
        console.warn(`Erreur lors de la lecture de l'animation pour ${appId}:`, error)
      }
    }
  }

  // Déclencher l'animation quand l'app est sélectionnée
  useEffect(() => {
    if (selectedApp) {
      // Petit délai pour s'assurer que la référence est disponible
      const timer = setTimeout(() => {
        playAnimation(selectedApp)
      }, 50)

      return () => clearTimeout(timer)
    }
  }, [selectedApp])

  const handleAppClick = (appId) => {
    // Déclencher l'animation immédiatement au clic
    playAnimation(appId)
    
    // Appeler onAppSelect pour changer la section
    onAppSelect(appId)
  }

  const handleAppHover = (appId) => {
    setHoveredApp(appId)
  }

  const handleAppLeave = () => {
    setHoveredApp(null)
  }

  const apps = [
    {
      id: 'house',
      name: 'Accueil',
      animation: houseAnimation,
      animationPath: '/house_animation.json'
    },
    {
      id: 'carts',
      name: 'Paniers',
      animation: cartsAnimation,
      animationPath: '/Cadis_animation.json'
    },
    {
      id: 'analytics',
      name: 'Statistiques',
      animation: analyticsAnimation,
      animationPath: '/Analytics_animation.json'
    },
    {
      id: 'social',
      name: 'Cam\'it',
      animation: socialAnimation,
      animationPath: '/Social media_animation.json'
    },
    {
      id: 'settings',
      name: 'Paramètres',
      animation: settingsAnimation,
      animationPath: '/settings_animation.json'
    }
  ]

  return (
    <div className={`app-dock-container ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="app-dock">
        {apps.map((app) => {
          const isSelected = selectedApp === app.id
          const isHovered = hoveredApp === app.id
          const scale = isHovered ? 1.1 : isSelected ? 1.08 : 1

          return (
            <div
              key={app.id}
              className={`app-dock-item ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={app.name}
              aria-current={isSelected ? 'page' : undefined}
              onClick={() => handleAppClick(app.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleAppClick(app.id)
                }
              }}
              onMouseEnter={() => handleAppHover(app.id)}
              onMouseLeave={handleAppLeave}
              style={{
                transform: `scale(${scale})`,
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div className="app-dock-icon">
                {app.animation && (
                  <Lottie
                    lottieRef={
                      app.id === 'house' ? houseLottieRef :
                      app.id === 'carts' ? cartsLottieRef :
                      app.id === 'analytics' ? analyticsLottieRef :
                      app.id === 'social' ? socialLottieRef :
                      app.id === 'settings' ? settingsLottieRef : null
                    }
                    animationData={app.animation}
                    loop={false}
                    autoplay={false}
                    style={{ width: '100%', height: '100%' }}
                    onComplete={() => {
                      // Arrêter l'animation à la fin et revenir au début
                      let lottieRef = null
                      if (app.id === 'house') {
                        lottieRef = houseLottieRef
                      } else if (app.id === 'carts') {
                        lottieRef = cartsLottieRef
                      } else if (app.id === 'analytics') {
                        lottieRef = analyticsLottieRef
                      } else if (app.id === 'social') {
                        lottieRef = socialLottieRef
                      } else if (app.id === 'settings') {
                        lottieRef = settingsLottieRef
                      }
                      
                      if (lottieRef && lottieRef.current) {
                        try {
                          // Utiliser l'API moderne de Lottie React
                          if (typeof lottieRef.current.goToAndStop === 'function') {
                            lottieRef.current.goToAndStop(0, true)
                          } else if (lottieRef.current.animationItem) {
                            // Fallback pour l'ancienne API
                            const instance = lottieRef.current.animationItem
                            if (instance && typeof instance.goToAndStop === 'function') {
                              instance.goToAndStop(0, true)
                            }
                          }
                        } catch (error) {
                          console.warn(`Erreur lors de l'arrêt de l'animation pour ${app.id}:`, error)
                        }
                      }
                    }}
                  />
                )}
              </div>
              <div className="app-dock-label">{app.name}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default AppDock

